import { Table } from '@dev-spendesk/grapes';
import React from 'react';

import { QuerySuspense } from 'common/components/QuerySuspense';
import { useTranslation } from 'common/hooks/useTranslation';
import { type TaxAccount } from 'modules/bookkeep/settings/accounting';
import { type QueryState } from 'src/core/api/queryState';

import { TableFooter } from './CommonFooter';
import { TaxAccountErrorWarning } from '../../TaxAccountErrorWarning';
import { TaxAccountSkeletonTable } from '../../TaxAccountSkeletonTable';

interface Props {
  taxAccountQueryState: QueryState<TaxAccount[]>;
}

export const DefaultReverseChargeAccountPullTable = ({
  taxAccountQueryState,
}: Props) => {
  const { t } = useTranslation('global');
  return (
    <QuerySuspense
      queryState={taxAccountQueryState}
      loading={<TaxAccountSkeletonTable />}
      fallback={() => <TaxAccountErrorWarning />}
    >
      {(taxAccounts) => {
        const reverseChargeAccounts = taxAccounts.filter(
          (account) => account.type === 'reverseCharge',
        );

        return (
          <Table<TaxAccount>
            rowHeight="compact"
            columns={[
              {
                id: 'vat-account-name',
                header: t('bookkeep.integrations.settings.vatTable.code'),
                width: '25%',
                renderCell(row) {
                  return row.type === 'reverseCharge'
                    ? (row.codeDebit ?? row.codeCredit)
                    : '';
                },
              },
              {
                id: 'vat-account-code',
                header: t(
                  'bookkeep.integrations.settings.vatTable.description',
                ),
                width: '75%',
                renderCell(row) {
                  return row.description ?? '';
                },
              },
            ]}
            data={reverseChargeAccounts}
            footer={
              taxAccounts.length === 0 ? (
                <TableFooter taxAccountQueryState={taxAccountQueryState} />
              ) : undefined
            }
            getRowId={(row) => row.id}
          />
        );
      }}
    </QuerySuspense>
  );
};
